import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useAppContext, useAppDispatch, useAppStore } from '@/hooks'
import { nodeRefreshTree, selectModeChange } from '@/store/modules/node/actions'

import { HeaderActions } from './HeaderActions'
import { HeaderBurgerMenu } from './HeaderBurgerMenu'
import { CollapsedHeader, Title, TitleAction } from './styles'

const NARROW_THRESHOLD = 260

type HeaderProps = {
	alphabet: boolean
	canReveal?: boolean
	isFullTreeLoading?: boolean
	isHidden?: boolean
	isRevealing?: boolean
	onHide?: () => void
	onRevealInTree?: () => void
	onShow?: () => void
	onTreeRefresh?: () => Promise<void>
	setAlphabet: (value: boolean) => void
}

export const Header = ({
	alphabet,
	canReveal,
	isFullTreeLoading,
	isHidden,
	isRevealing,
	onHide,
	onRevealInTree,
	onShow,
	onTreeRefresh,
	setAlphabet,
}: HeaderProps) => {
	const { t } = useAppContext()
	const dispatch = useAppDispatch()
	const selectMode = useAppStore((store) => store.node.selectMode)

	const [refreshing, setRefreshing] = useState(false)
	const [isNarrow, setIsNarrow] = useState(false)
	const headerRef = useRef<HTMLDivElement>(null)

	// Check initial width and observe changes
	useEffect(() => {
		const element = headerRef.current
		if (!element) return

		// Check initial width immediately
		const checkWidth = () => {
			const width = element.getBoundingClientRect().width
			setIsNarrow(width < NARROW_THRESHOLD)
		}

		// Check on mount
		checkWidth()

		// Observe for changes
		const observer = new ResizeObserver(() => {
			checkWidth()
		})

		observer.observe(element)
		return () => observer.disconnect()
	}, [isHidden]) // Re-run when visibility changes

	const handleRefresh = useCallback(async () => {
		setRefreshing(true)
		if (onTreeRefresh) {
			await onTreeRefresh()
		} else {
			await dispatch(nodeRefreshTree())
		}
		setRefreshing(false)
	}, [dispatch, onTreeRefresh])

	const handleSelectModeChange = useCallback(() => {
		dispatch(selectModeChange(!selectMode))
	}, [dispatch, selectMode])

	const handleAlphabetChange = useCallback(() => {
		setAlphabet(!alphabet)
	}, [alphabet, setAlphabet])

	// Collapsed state - show only expand button
	if (isHidden) {
		return (
			<CollapsedHeader role="heading">
				<TitleAction
					onClick={onShow}
					role="button"
					style={{ margin: 0 }}
					title={t('SHOW_TREE')}
				>
					<FontAwesomeIcon icon={faChevronRight} />
				</TitleAction>
			</CollapsedHeader>
		)
	}

	const sharedProps = {
		alphabet,
		canReveal,
		isFullTreeLoading,
		isRevealing,
		onAlphabetChange: handleAlphabetChange,
		onRefresh: handleRefresh,
		onRevealInTree,
		onSelectModeChange: handleSelectModeChange,
		refreshing,
		selectMode,
	}

	return (
		<Title ref={headerRef} role="heading">
			{onHide && (
				<TitleAction
					onClick={onHide}
					role="button"
					style={{ marginRight: isNarrow ? 0 : 'auto', marginLeft: 0 }}
					title={t('HIDE_TREE')}
				>
					<FontAwesomeIcon icon={faChevronLeft} />
				</TitleAction>
			)}
			{isNarrow ? (
				<HeaderBurgerMenu {...sharedProps} />
			) : (
				<HeaderActions {...sharedProps} />
			)}
		</Title>
	)
}

